// Fires the mark-read call for whichever thread a screen is actually showing.
//
// `useFollowBottom` already knows whether the reader is at the bottom — the same boolean
// that decides whether to auto-scroll them. This is what a screen DOES with it: whenever
// it is true, including the initial open (which always counts as "at the bottom"), report
// the newest message this client has loaded as the reader's own read position. That covers
// both "just opened the thread" and "sitting at the bottom watching messages arrive", and
// naturally skips marking anything while the reader has scrolled up into history.
//
// A thread with no messages loaded yet has nothing to report — `"0"`, matching the column's
// own default, rather than omitting the call or sending an empty string.
//
// ONLY THE THREAD ACTUALLY ON SCREEN CALLS THIS. `usePlan` loads its main thread regardless
// of which tab is selected, but `Conversation` — the only caller for that thread — is only
// ever mounted while its tab is the one showing (see `PlanScreen`'s conditional rendering).
// The same is true of `PrivateChat` and whichever private thread it has been handed. So
// there is no separate "is this the active tab" check here: a component that is not on
// screen is not mounted, and a hook that is not mounted does not fire.
//
// Fires at most once per (thread, newest-seq) pair, tracked in a ref rather than through a
// dependency comparison alone, so a re-render that leaves both unchanged costs nothing.
//
// ---------------------------------------------------------------------------
// THE THIRD ARGUMENT `markRead` TAKES
// ---------------------------------------------------------------------------
//
// `usePlan.ts`'s `markRead` cannot, on its own, tell whether a message newer than what it
// is about to report as read has shown up for THIS thread by the time its response lands —
// it owns the badge (`PlanSnapshot.threads`), not every thread's own message list. For the
// group conversation those are the same thing (`usePlan` loads it), but `PrivateChat`'s own
// thread is read by `usePrivateThread`, on its own independent refresh cycle, entirely
// outside `usePlan`'s snapshot.
//
// This hook, though, is always given the right `messages` array for whichever thread it is
// tracking — group or private — by whichever surface called it. So it is what answers "is
// anything newer than `seq` already known", and it answers that question FRESH, at the
// moment `markRead`'s response actually arrives, via a ref that mirrors `messages` on every
// render rather than the value captured when the call was fired. A message that arrived in
// between — from either thread kind, by the same mechanism — is caught by this, not by
// guessing.

import { useEffect, useRef } from 'react';

import type { Message } from '../api/contract.js';

export type MarkThreadRead = (
  threadId: string,
  seq: string,
  /** The newest seq THIS hook currently knows about for `threadId`, read live. */
  newestKnownSeq: () => string | undefined,
) => void;

export function useMarkThreadRead(
  threadId: string | null,
  messages: readonly Message[],
  atBottom: boolean,
  markRead: MarkThreadRead,
): void {
  const lastSent = useRef<string | null>(null);
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  });

  useEffect(() => {
    if (threadId === null || !atBottom) {
      return;
    }
    const seq = messages[messages.length - 1]?.seq ?? '0';
    const key = `${threadId}:${seq}`;
    if (lastSent.current === key) {
      return;
    }
    lastSent.current = key;
    markRead(
      threadId,
      seq,
      () => messagesRef.current[messagesRef.current.length - 1]?.seq,
    );
  }, [threadId, messages, atBottom, markRead]);
}
